import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createDeepLinkingPageEvent } from '../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../analytics/functions';
import { isSupportedMobileBrowser } from '../../base/environment/environment';
import { translate } from '../../base/i18n/functions';
import Platform from '../../base/react/Platform.web';
import { withPixelLineHeight } from '../../base/styles/functions.web';
import Button from '../../base/ui/components/web/Button';
import { openWebApp } from '../actions';
import { _TNS } from '../constants';
import { generateDeepLinkingURL } from '../functions';
const PADDINGS = {
    topBottom: 180,
    leftRight: 0
};
const useStyles = makeStyles()((theme) => {
    return {
        container: {
            background: '#f7f7f7',
            width: '100vw',
            height: '100dvh',
            overflowX: 'hidden',
            overflowY: 'auto',
            justifyContent: 'center',
            display: 'flex',
            '& a': {
                textDecoration: 'none'
            }
        },
        contentWrapper: {
            border: '1px solid #e5e5e5',
            borderRadius: '8px',
            padding: '60px 40px',
            background: '#fff',
            maxWidth: '85%'
        },
        contentPane: {
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            padding: `${PADDINGS.topBottom}px ${PADDINGS.leftRight}px`,
            maxWidth: '80%',
            width: '400px',
            color: '#777'
        },
        launchingMeetingLabel: {
            marginTop: 24,
            textAlign: 'center',
            marginBottom: 32,
            ...withPixelLineHeight(theme.typography.heading5)
        },
        roomNameLabel: {
            ...withPixelLineHeight(theme.typography.bodyLongRegularLarge)
        },
        joinMeetWrapper: {
            marginTop: 24,
            width: '100%'
        },
        labelDescription: {
            marginTop: 35,
            ...withPixelLineHeight(theme.typography.bodyShortRegularLarge),
            textAlign: 'center',
            fontSize: '14px',
        },
        labelWithMargin: {
            marginTop: 20,
            marginBottom: 20,
            ...withPixelLineHeight(theme.typography.bodyShortRegularLarge)
        },
        linkWrapper: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 8,
            width: '100%'
        },
        troubleshootingGuideWrapper: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 35,
            marginBottom: 15,
            width: '100%'
        },
        textCenter: {
            textAlign: 'center',
            ...withPixelLineHeight(theme.typography.bodyShortRegularLarge)
        },
        linkLabel: {
            marginTop: 8,
            color: '#7f86f7',
            ...withPixelLineHeight(theme.typography.bodyLongBoldLarge),
            fontSize: '14px',
            fontWeight: 'normal',
        },
        supportedBrowserContent: {
            marginTop: 35,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
        },
        labelOr: {
            ...withPixelLineHeight(theme.typography.bodyShortRegularLarge)
        },
        separator: {
            marginTop: '32px',
            height: 1,
            width: `calc(100% + ${2 * PADDINGS.leftRight}px)`,
            background: theme.palette.ui03
        },
        colorRed: {
            color: '#F44336'
        },
        primaryButton: {
            padding: '10px 20px',
        },
        joinNowBtn: {
            minWidth: '180px',
            fontSize: '16px'
        }
    };
});
const DeepLinkingMobilePage = ({ t }) => {
    const deeplinkingCfg = useSelector((state) => state['features/base/config']?.deeplinking || {});
    const { hideLogo } = deeplinkingCfg;
    const deepLinkingUrl = useSelector(generateDeepLinkingURL);
    const room = useSelector((state) => decodeURIComponent(state['features/base/conference'].room || ''));
    const url = useSelector((state) => state['features/base/connection'] || {});
    const dispatch = useDispatch();
    const { classes: styles } = useStyles();
    const troubleShootingGuideURL = useCallback(() => {
        return 'https://therabyte.freshdesk.com/a/solutions/articles/29000048638';
    }, [deeplinkingCfg]);
    const generateDownloadURL = useCallback(() => {
        const { downloadLink, dynamicLink, appScheme } = (deeplinkingCfg?.[Platform.OS] || {});
        if (downloadLink && typeof dynamicLink === 'undefined') {
            return downloadLink;
        }
        const { apn, appCode, customDomain, ibi, isi } = dynamicLink || {};
        const domain = customDomain ?? `https://${appCode}.app.goo.gl`;
        return `${domain}/?link=${encodeURIComponent(window.location.href)}&apn=${apn}&ibi=${ibi}&isi=${isi}&ius=${appScheme}&efr=1`;
    }, [deeplinkingCfg]);
    const onDownloadApp = useCallback(() => {
        sendAnalytics(createDeepLinkingPageEvent('clicked', 'downloadAppButton', { isMobileBrowser: true }));
    }, []);
    const onLaunchWeb = useCallback(() => {
        sendAnalytics(createDeepLinkingPageEvent('clicked', 'launchWebButton', { isMobileBrowser: true }));
        dispatch(openWebApp());
    }, []);
    const onOpenApp = useCallback(() => {
        sendAnalytics(createDeepLinkingPageEvent('clicked', 'openAppButton', { isMobileBrowser: true }));
    }, []);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const toggleCollapse = () => setIsCollapsed(prev => !prev);
    const onOpenLinkProperties = useMemo(() => {
        const { downloadLink } = (deeplinkingCfg?.[Platform.OS] || {});
        if (downloadLink) {
            return {
            // When opening a link to the download page, we want to let the
            // OS itself handle intercepting and opening the appropriate
            // app store. This avoids potential issues with browsers, such
            // as iOS Chrome, not opening the store properly.
            };
        }
        return {
            // When falling back to another URL (Firebase) let the page be
            // opened in a new window. This helps prevent the user getting
            // trapped in an app-open-cycle where going back to the mobile
            // browser re-triggers the app-open behavior.
            target: '_blank',
            rel: 'noopener noreferrer'
        };
    }, [deeplinkingCfg]);
    useEffect(() => {
        sendAnalytics(createDeepLinkingPageEvent('displayed', 'DeepLinkingMobile', { isMobileBrowser: true }));
    }, []);
    return (React.createElement("div", { className: styles.container },
        React.createElement("div", { className: styles.contentPane },
            React.createElement("div", { className: styles.contentWrapper },
                React.createElement("div", { className: styles.textCenter }, !hideLogo && (React.createElement("img", { style: { width: '300px', maxWidth: '90%' }, alt: t('welcomepage.logo.logoDeepLinking'), src: 'images/logo-deep-linking.webp' }))),
                React.createElement("div", { className: styles.labelDescription },
                    React.createElement("p", null, "You are almost there. Click the button below to access your meeting.")),
                isSupportedMobileBrowser() ? (React.createElement("div", { className: styles.supportedBrowserContent },
                    React.createElement("a", { className: styles.linkWrapper, onClick: onLaunchWeb, target: '_top' },
                        React.createElement(Button, { className: styles.joinNowBtn, label: 'Join Meeting Now' })))) : (React.createElement("div", { className: styles.labelDescription }, t(`${_TNS}.unsupportedBrowser`))),
                React.createElement("a", { ...onOpenLinkProperties, className: styles.troubleshootingGuideWrapper, href: troubleShootingGuideURL(), target: '_blank' },
                    React.createElement("div", { className: styles.linkLabel }, t(`${_TNS}.troubleshootingGuide`))),
                React.createElement("div", { role: "button", onClick: toggleCollapse, className: styles.textCenter, style: { cursor: 'pointer', fontSize: '12px' } }, t(`${_TNS}.legacyAccessTitle`)),
                !isCollapsed && (React.createElement("div", { className: 'collapsibleWrapper' },
                    React.createElement("div", { className: styles.labelWithMargin },
                        React.createElement("p", null, "Use the Jitsi Meet app if you are using an older device or if you are having trouble joining from your current web browser."),
                        React.createElement("p", { className: styles.colorRed },
                            "Please note: You will need to ",
                            React.createElement("strong", null, "install"),
                            " the Jitsi Meet app ",
                            React.createElement("strong", null, "prior to joining your meeting"),
                            ".")),
                    React.createElement("a", { ...onOpenLinkProperties, className: styles.troubleshootingGuideWrapper, href: deepLinkingUrl, onClick: onOpenApp, target: '_top' },
                        React.createElement(Button, { label: 'Join with Jitsi App', type: 'primaryPlain' })),
                    React.createElement("div", { className: styles.textCenter }, "or"),
                    React.createElement("a", { ...onOpenLinkProperties, className: styles.linkWrapper, href: generateDownloadURL(), onClick: onDownloadApp, target: '_top' },
                        React.createElement(Button, { label: 'Download App', type: 'infoPlain' }))))))));
};
export default translate(DeepLinkingMobilePage);
